import {
  all,
  call,
  getContext,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';
import { ItineraryApiClient } from '../../../api/itinerary';
import {
  ItineraryResponse,
  ItineraryStatus,
  UpdateItineraryRequest,
} from '../../../models/itinerary';
import { ActionTypes } from '../../constants/action-types';
import {
  getUserNote,
  getUserResponseStatus,
} from '../confirm-dialog/selectors';
import { MetricAction } from '../metrics/types';
import {
  getSelectedOptions,
  getSelectedCombinations,
} from '../selected-options/selectors';
import { SelectOptionWithRateAction } from '../selected-options/types';
import { UpdateSegmentRequest } from './../../../models/itinerary';
import {
  ItineraryErrorAction,
  ItineraryReceiveAction,
  ItineraryRequestChangesErrorAction,
  ItineraryRequestChangesResponseAction,
} from './types';

export function* watcherSaga() {
  yield all([
    takeLatest(ActionTypes.ITINERARY_GET_REQUEST, workerGetRequestSaga),
    takeLatest(
      ActionTypes.ITINERARY_REQUEST_CHANGES_REQUEST,
      workerRequestChangesSaga,
    ),
  ]);
}

function* workerRequestChangesSaga(): Generator<any, any, any> {
  try {
    const api: ItineraryApiClient = yield getContext('api');
    const searchParams = new URLSearchParams(window.location.search);
    const id: string = searchParams.get('id') || '';
    const pin: string = searchParams.get('pin') || '';
    const ssi = searchParams.get('ssi') || false;
    const userResponse: ItineraryStatus = yield select(getUserResponseStatus);
    const userNote: string = yield select(getUserNote);
    const selectedSegments: UpdateSegmentRequest[] = yield select(
      getSelectedOptions,
    );

    const request: UpdateItineraryRequest = {
      note: userNote,
      status: userResponse,
      selectedOptions:
        userResponse === 'Rejected' || ssi
          ? undefined
          : {
              segments: Object.values(selectedSegments),
            },
      selectedCombinations: ssi
        ? yield select(getSelectedCombinations)
        : undefined,
      saviSelectIntl: ssi ? true : false,
    };

    const response: any = yield call(api.updateItinerary, id, pin, request);

    if (response.status !== 200) throw new Error(`${response.status}`);

    const receiveAction: ItineraryRequestChangesResponseAction = {
      type: ActionTypes.ITINERARY_REQUEST_CHANGES_RESPONSE,
    };
    yield put(receiveAction);
    yield put({ type: ActionTypes.RECORD_METRIC });
  } catch (error) {
    const errorAction: ItineraryRequestChangesErrorAction = {
      type: ActionTypes.ITINERARY_REQUEST_CHANGES_ERROR,
      error,
    };

    yield put(errorAction);
  }
}

export function* workerGetRequestSaga(): Generator<any, any, any> {
  try {
    const api: ItineraryApiClient = yield getContext('api');

    const searchParams = new URLSearchParams(window.location.search);

    const id = searchParams.get('id') || '';
    const pin = searchParams.get('pin') || '';
    const ssi = searchParams.get('ssi') || false;

    let response;

    if (ssi) {
      response = yield call(api.getIntlItinerary, id, pin);
    } else {
      response = yield call(api.getItinerary, id, pin);
    }

    if (response.status !== 200) throw new Error(`${response.status}`);

    const itinerary: ItineraryResponse = response.data;

    if (!itinerary) throw new Error('404');

    const receiveAction: ItineraryReceiveAction = {
      type: ssi
        ? ActionTypes.ITINERARY_INTL_GET_RECEIVE
        : ActionTypes.ITINERARY_GET_RECEIVE,
      data: itinerary,
    };

    yield put(receiveAction);

    if (ssi) {
      return;
    }

    if (itinerary.status === 'New') {
      const action: MetricAction = {
        type: ActionTypes.RECORD_METRIC,
        value: 'new',
      };
      yield put(action);
    }

    // Check if we need to restore the selected segments
    if (itinerary.status !== 'New' && itinerary.selectedSegments) {
      const selectActions = itinerary.selectedSegments.map(
        (selectedSegment) => {
          const baseAction = {
            type: ActionTypes.OPTION_SELECT_WITH_RATE,
            isRecommended: false,
            segmentId: selectedSegment.index,
          };
          switch (selectedSegment.segmentType) {
            case 'Air':
              const selectAirSegmentOption: SelectOptionWithRateAction = {
                ...baseAction,
                option: selectedSegment.airSegment!,
              };
              return put(selectAirSegmentOption);
            case 'Car':
              const selectCarSegmentOption: SelectOptionWithRateAction = {
                ...baseAction,
                option: selectedSegment.carSegment!,
              };
              return put(selectCarSegmentOption);
            case 'Hotel':
              const selectHotelSegmentOption: SelectOptionWithRateAction = {
                ...baseAction,
                option: selectedSegment.hotelSegment!,
              };
              return put(selectHotelSegmentOption);
          }
        },
      );
      yield all(selectActions);
    }
  } catch (error) {
    console.error(error);
    const errorAction: ItineraryErrorAction = {
      type: ActionTypes.ITINERARY_GET_ERROR,
      error,
    };

    yield put(errorAction);
  }
}
